import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import WillView from "./pages/WillView";
import SignIn from "./pages/SignIn";
import Login from "./pages/Login";
import CreateWill from "./pages/CreateWill";
import NotFound from "./pages/NotFound";
import EditWill from "./pages/EditWill";
import MakeOffer from "./pages/MakeOffer";
import MyWants from "./pages/MyWants";
import MyGenies from "./pages/MyGenies";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './pages/AuthCallback';
import MyOffers from "./pages/MyOffers";
import OfferDetails from "./pages/OfferDetails";
import ProviderOfferDetails from "./pages/ProviderOfferDetails";
import { useEffect, useState } from 'react';
import VerifyEmail from "./pages/VerifyEmail";
import { LanguageProvider } from './contexts/LanguageContext';
import { ScrollPositionProvider } from './contexts/ScrollPositionContext';
import OfferView from './pages/OfferView';
import { AuthProvider } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { setupCSP } from './utils/securityUtils';
import { 
  initializeAuth, 
  isAuthenticated, 
  getAccessToken
} from './services/authService';
import { AuthDebugger } from './components/AuthDebugger';
import { authChannel } from './utils/authChannel';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiesPolicy from './pages/CookiesPolicy';
import LearnMore from './pages/LearnMore';
import Chat from './pages/Chat';
import Feed from './pages/Feed';
import Search from './pages/Search';
import AppLayout from "./components/AppLayout";

const queryClient = new QueryClient();

function App() {
  const [isAppReady, setIsAppReady] = useState(false);
  
  useEffect(() => {
    if ('Notification' in window) {
      Notification.requestPermission();
    }
    setupCSP();
    const initAuth = async () => {
      try {
        await initializeAuth();
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setIsAppReady(true);
      }
    };
    initAuth();
  }, []);

  if (!isAppReady) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <AuthProvider>
      <ProfileProvider>
        <ScrollPositionProvider>
          <LanguageProvider>
            <QueryClientProvider client={queryClient}>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <Routes>
                    {/* Default route - Instagram-style Feed */}
                    <Route path="/" element={
                      <AppLayout>
                        <Feed />
                      </AppLayout>
                    } />
                    
                    {/* Original Index moved to /feed */}
                    <Route path="/feed" element={
                      <AppLayout>
                        <Index />
                      </AppLayout>
                    } />
                    
                    {/* Original routes with NavBar */}
                    <Route path="/will/:id" element={
                      <AppLayout>
                        <WillView />
                      </AppLayout>
                    } />
                    <Route path="/signin" element={
                      <AppLayout>
                        <SignIn />
                      </AppLayout>
                    } />
                    <Route path="/login" element={
                      <AppLayout>
                        <Login />
                      </AppLayout>
                    } />
                    <Route 
                      path="/profile" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Profile />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/profile/:userId" 
                      element={
                        <AppLayout>
                          <Profile />
                        </AppLayout>
                      } 
                    />
                    <Route 
                      path="/profile/edit" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <EditProfile />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/create-will" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <CreateWill />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/will/:id/edit" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <EditWill />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/offer/:id" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <MakeOffer />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/my-wants" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <MyWants />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/my-genies" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <MyGenies />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/my-genies/:id" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <OfferDetails />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/login/oauth2/code/google" element={<AuthCallback />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route 
                      path="/my-offers" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <MyOffers />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/my-offers/:id" 
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <ProviderOfferDetails />
                          </AppLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/verify-email" element={
                      <AppLayout>
                        <VerifyEmail />
                      </AppLayout>
                    } />
                    <Route path="/offer/:willId" element={
                      <AppLayout>
                        <OfferView />
                      </AppLayout>
                    } />
                    <Route path="/auth/*" element={<AuthCallback />} />
                    
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/cookies" element={<CookiesPolicy />} />
                    <Route path="/learn-more" element={
                      <AppLayout>
                        <LearnMore />
                      </AppLayout>
                    } />
                    
                    <Route path="/chat/*" element={
                      <ProtectedRoute>
                        <AppLayout>
                          <Chat />
                        </AppLayout>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="/caht/*" element={<Navigate to="/chat" replace />} />
                    
                    <Route path="/search" element={
                      <AppLayout>
                        <Search />
                      </AppLayout>
                    } />
                    
                    <Route path="*" element={
                      <AppLayout>
                        <NotFound />
                      </AppLayout>
                    } />
                  </Routes>
                  {import.meta.env.DEV && <AuthDebugger />}
                </BrowserRouter>
              </TooltipProvider>
            </QueryClientProvider>
          </LanguageProvider>
        </ScrollPositionProvider>
      </ProfileProvider>
    </AuthProvider>
  );
}

export default App;
